/**
 * CAPTURE-channel payload builder. The TYPE has moved to
 * `@kraahl/contract` as a discriminated union — see
 * `packages/contract/src/checkout-payload.ts`. This file owns the
 * web-side builder that maps cart state (CartItemData[] + ShippingFormData)
 * into the canonical {@link CheckoutPayload} shape.
 *
 * Why builder lives here and not in the contract package: the inputs
 * (`CartItemData`, `ShippingFormData`) are web-only — they pull in React
 * hooks, image-shape types, etc. Contract package stays framework-free.
 *
 * MISU history. Before 2026-06-26 the Payload was a FLAT interface with
 * `externalBySeller?: Record<sellerId, {...}>` — optional, so "live-rate
 * checkout without externalShipping" was a representable state. That
 * representable state shipped in production on the 14h window between
 * adfee9da and 22a4a5bf (see HANDOFF_EXTERNAL_SHIPPING_CAPTURE_GAP_2026_06_26).
 *
 * The Payload is now a discriminated union on `kind: "flat_rate" | "live_rate"`.
 * `live_rate` REQUIRES `externalShipping`; `flat_rate` cannot carry one.
 * The builder decides the kind from whether the optional 7th arg was passed:
 *   - `externalShipping` present → `kind: "live_rate"`
 *   - `externalShipping` absent  → `kind: "flat_rate"`
 *
 * Sibling-E (`.tondocs/RELIABILITY_ROADMAP.md §3a`): never fork the builder.
 * Add a parameter — variance axes live on THIS function. Forks dropped
 * `currency` once and broke the Stripe rail on every auction/offer checkout.
 */

import type { CartItemData } from "@/components/cart/types"
import { asRawImageRef } from "@/lib/storage/public-url"

/** "no image" sentinel in the branded domain — the contract field is non-null. */
const EMPTY_IMAGE_REF = asRawImageRef("")
import type { ShippingFormData } from "@/components/cart/shipping-form"
import type { OrderCreationContext } from "@/lib/order-creation"
import type { CarrierProvider, CheckoutPayload } from "@kraahl/contract"

// Re-export the canonical type so existing imports (`@/lib/checkout-payload`)
// keep working. New code is welcome to import from `@kraahl/contract` directly.
export type { CheckoutPayload } from "@kraahl/contract"

/**
 * Single-seller view of the live-rate selection — what the buyer-side click
 * handler has at hand before it calls {@link buildCheckoutPayload}. The
 * builder lifts this onto the `kind: "live_rate"` branch as a root-level
 * `externalShipping` + `carrierProvider` pair (matches the Intent shape).
 *
 * `sellerId` is carried for historical reasons (the pre-MISU shape was a
 * `Record<sellerId, ...>`); under the single-seller invariant it's always
 * equal to `intent.sellerId` and the builder doesn't read it. Future
 * cleanup can drop the field once every callsite stops threading it.
 *
 * Field-name discipline (Sibling-F · F12, 2026-06-27): `amount` is the
 * canonical Intent-side name and now the only name used at the view-type
 * layer. Pre-F12 the view-type carried a divergent name and the builder
 * bridged the two — two names for one axis is the seed of the next drift.
 * Fitness pin `tests/unit/fitness-no-estimate-amount.test.ts` keeps it
 * dead; the legacy `@deprecated` back-compat arm in `lib/order-creation.ts`
 * (mirrors pre-MISU stored JSON; dies with PR-L) and the persisted SQL
 * column `external_rate_estimate_amount` (column rename = migration, out
 * of F12 scope) are explicitly allowlisted there.
 */
export interface CheckoutPayloadExternalShipping {
  sellerId: string
  shipmentId: string
  rateId: string
  amount: number
  carrierProvider: CarrierProvider
}

/**
 * Build the order payload sent to capture route for server-side order creation.
 * Single canonical implementation used by ALL checkout paths — cart, chat,
 * auction-winner sheet, offer sheet.
 *
 * Returns the discriminated {@link CheckoutPayload}: pass `externalShipping`
 * → `kind: "live_rate"`; omit it → `kind: "flat_rate"`. The two branches
 * carry DIFFERENT fields at the type level — consumers (`createOrder`,
 * `buildOrderPayloadFromCapture`) switch on `payload.kind` exhaustively.
 *
 * Defaults `context` to `{ kind: "listing" }` — cart + chat checkouts are
 * always against fixed-price listings. Auction-winner and offer sheets pass
 * `{ kind: "auction" }` / `{ kind: "offer", offerId }` explicitly.
 *
 * Sibling-E: NEVER fork. Add a parameter; the forks lock-step silent-fail.
 */
export function buildCheckoutPayload(
  userId: string | null,
  items: CartItemData[],
  formData: ShippingFormData,
  currencyCode: string,
  totals: { subtotal: number; shippingTotal: number; serviceFee: number; total: number },
  context: OrderCreationContext = { kind: "listing" },
  externalShipping?: CheckoutPayloadExternalShipping | null,
): CheckoutPayload {
  // PR-E Move 4 (Sibling-F · F2): CheckoutPayload structurally extends
  // CheckoutIntent. Single-seller invariant — every item shares one seller,
  // surfaced at root as `sellerId` to mirror `CheckoutIntent.sellerId`. The
  // builder derives it from the first item; downstream callers always group
  // by seller before reaching this builder so all items agree.
  const sellerId = items[0]?.sellerId ?? "";

  // Per-item capture-time snapshot (rich fields the buyer saw at checkout).
  // Match the {@link CheckoutPayloadItemSnapshot} shape from the contract.
  const itemSnapshot = (i: CartItemData) => ({
    price: i.price,
    currency: i.currency || currencyCode,
    name: i.name,
    description: i.description,
    condition: i.condition,
    grade: i.grade,
    // RAW on purpose — this snapshot is persisted verbatim into
    // `order_items.image_url` / the order's seller display. Resolving here
    // would push absolute URLs back into the DB (handoff §9).
    imageUrl: i.images[0] || EMPTY_IMAGE_REF,
    sellerId: i.sellerId,
    sellerName: i.sellerName,
    sellerAvatarUrl: i.sellerAvatarUrl || EMPTY_IMAGE_REF,
    selectedShippingMethod: i.selectedShippingMethod || null,
  });

  const base = {
    userId,
    currency: currencyCode,
    sellerId,
    // PR-E Move 1 (Sibling-F · F2): collapsed `shippingInfo` →
    // `shippingAddress` (canonical `CheckoutIntentAddress` shape). The
    // composed `phone` field is gone — consumers (email payloads,
    // order-creation RPC payload) recompose it from
    // `phoneCountryCode + phoneNumber` already.
    shippingAddress: {
      address: formData.address,
      apartment: formData.apartment,
      city: formData.city,
      postalCode: formData.postalCode,
      country: formData.country,
      state: formData.state,
      email: formData.email,
      phoneCountryCode: formData.phoneCountryCode,
      phoneNumber: formData.phoneNumber,
      firstName: formData.firstName,
      lastName: formData.lastName,
    },
    // CAPTURED_FACTS Phase E (2026-07-11): cart-math totals are NO LONGER
    // put on the wire — the server derives orders.{subtotal,shipping,
    // service_fee,total} at capture from validated items + the CREATE-time
    // wire snapshot. The `totals` param is retained (callers still compute it
    // for the cart UI) but intentionally not assigned to the payload.
    // @see .tondocs/payments/CAPTURED_FACTS_PHASE_E_MINISPEC.md §1 (E6), §3
    context,
  };

  if (externalShipping) {
    // PR-E Move 4: live_rate items mirror `CheckoutIntentLiveRateItem`
    // (carries `listingId` / `quantity` / `context`) + snapshot.
    const liveItems = items.map((i) => ({
      listingId: i.listingId,
      quantity: i.quantity,
      context,
      ...itemSnapshot(i),
    }));
    return {
      ...base,
      kind: "live_rate",
      items: liveItems,
      externalShipping: {
        rateId: externalShipping.rateId,
        shipmentId: externalShipping.shipmentId,
        amount: externalShipping.amount,
      },
      carrierProvider: externalShipping.carrierProvider,
    };
  }

  // PR-E Move 4: flat_rate items mirror `CheckoutIntentFlatRateItem`
  // (adds `shippingMethodId` + `context`) + snapshot. We expect every
  // flat_rate cart line to have a selected shipping method; if it's
  // missing we fall back to "" so the row still parses (server re-derives
  // shipping via `shipping_config` and rejects on mismatch).
  const flatItems = items.map((i) => ({
    listingId: i.listingId,
    quantity: i.quantity,
    shippingMethodId: i.selectedShippingMethod?.id ?? "",
    context,
    ...itemSnapshot(i),
  }));
  return {
    ...base,
    kind: "flat_rate",
    items: flatItems,
  };
}
